import React from 'react';
import { Link } from 'react-router';
import { LEGAL_DOCUMENTS } from '@lds-efsp/shared-contracts';
import { useTermsNotice } from './useTermsNotice';

/**
 * The document this notice is about, named from the registry rather than
 * spelled here: the registry is the sole source of a legal document's title,
 * so the notice and the page it links to can never disagree about what they
 * are called.
 */
const TERMS_TITLE = LEGAL_DOCUMENTS.find((entry) => entry.id === 'terms')?.title ?? 'Terms of Service';

/**
 * Populated-state presentation, applied ONLY while populated: an empty
 * container must hand layout no padding, border or margin at all.
 */
const PRESENTATION_CLASSES =
  'mx-auto mt-4 w-full max-w-3xl rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900';

/**
 * The always-mounted Terms notice (SOR-421) — the dismissible, non-blocking
 * successor to the re-acceptance interstitial.
 *
 * The container is the surface's whole structural contract, so it is declared
 * here and never delegated: it carries a stable `id`, the same value as its
 * `data-testid`, and `role="status"` from MOUNT — before it has anything to
 * announce and whether or not it ever will. A live region announces reliably
 * only when it is already in the document before it receives content, so the
 * role is unconditional, the container is never `display:none` / `hidden`, it
 * is never conditionally rendered, and dismissing a notice empties it rather
 * than removing it. What varies is the CONTENT, never the container.
 *
 * While populated the notice names the Terms of Service, states the announced
 * version's effective date (which may still be in the future — a version can
 * be posted before it governs), links to that text, and offers exactly one
 * action: dismiss. There is no checkbox, no accept button and nothing the
 * customer must do to keep using the application; dismissal records that
 * notice was given and locks nothing. Privacy and accessibility are never
 * named — this notice is the Terms notice and only ever announces a Terms
 * version.
 *
 * The link and the sentence both come from the presentation the hook's shared
 * decider answered for the announced version at the instant the session's read
 * pinned, so the two always describe the same version: while that version is
 * still ahead of its effective date the notice says the change is pending and
 * leads to the announced text's own upcoming route, and only once the version
 * governs does it say the Terms have been updated and lead to the document's
 * own public route. The surface reads no clock and derives no standing of its
 * own — a version stops being announced because the decider stops answering
 * it, not because this file noticed a date.
 *
 * The surface takes no props: it reads the one verdict the session has and
 * decides nothing itself. It imports none of the blocking-message machinery —
 * the wizard's blocking regions answer a different question (a step refusing
 * to advance) and their role split is a property of that component's contract.
 */
export const TermsNotice: React.FC = () => {
  const { verdict, presentation, dismiss, dismissalError, containerRef } = useTermsNotice();

  const populated = verdict.due;
  // The registry pins every effective date to `YYYY-MM-DDTHH:mm:ss.sssZ`, so
  // the calendar date the customer reads is its first ten characters.
  const effectiveDate = verdict.effectiveDate ? verdict.effectiveDate.slice(0, 10) : null;

  return (
    <div
      ref={containerRef}
      role="status"
      id="terms-notice"
      data-testid="terms-notice"
      // Focus lands here when the notice arrives; -1 keeps it out of the tab
      // order, so the next Tab moves on rather than staying put.
      tabIndex={-1}
      className={populated ? PRESENTATION_CLASSES : undefined}
    >
      {populated && (
        <>
          <p className="font-semibold">{TERMS_TITLE}</p>
          {effectiveDate && <p>Effective {effectiveDate}</p>}
          <p className="mt-2">
            {presentation.wording === 'pending-change' ? (
              <>
                A new {TERMS_TITLE} takes effect on {effectiveDate}. You can keep using the
                application as you do today.
              </>
            ) : (
              <>The {TERMS_TITLE} have been updated. You can keep using the application as you do today.</>
            )}
          </p>
          <p className="mt-2">
            {presentation.linkTarget === 'upcoming' ? (
              <Link to="/legal/terms/upcoming" className="font-medium underline">
                Read the upcoming {TERMS_TITLE}
              </Link>
            ) : (
              <Link to="/legal/terms" className="font-medium underline">
                Read the {TERMS_TITLE}
              </Link>
            )}
          </p>
          <button
            type="button"
            onClick={() => {
              if (verdict.version !== null) void dismiss(verdict.version);
            }}
            className="mt-3 rounded-md border border-amber-400 bg-white px-3 py-1 font-medium"
            data-testid="terms-notice-dismiss"
          >
            Dismiss
          </button>
          {dismissalError && (
            <p className="mt-3" data-testid="terms-notice-error">
              {dismissalError}
            </p>
          )}
        </>
      )}
    </div>
  );
};
